// client/src/pages/Weddings/steps/EventDateStep.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Grid,
  Button,
  Alert,
  IconButton,
  Chip,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import TodayIcon from "@mui/icons-material/Today";
import { useWeddingWizard } from "../../../context/WeddingWizardContext";
import api from "../../../services/api";

const TZ = "America/St_Johns";
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatDay(date, timeZone = TZ) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .formatToParts(date)
    .reduce((acc, p) => ((acc[p.type] = p.value), acc), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}
function toParts(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return { y, m, d };
}
function buildMonthGrid(year, month) {
  const firstOfMonth = new Date(Date.UTC(year, month - 1, 1, 12, 0, 0));
  const firstDow = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    weekday: "short",
  }).format(firstOfMonth);
  const dowOrder = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const offset = dowOrder.indexOf(firstDow);
  const gridStart = new Date(firstOfMonth.getTime() - offset * 86400000);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart.getTime() + i * 86400000);
    const ymd = formatDay(d);
    const inTargetMonth = Number(ymd.slice(5, 7)) === month;
    cells.push({ day: ymd, inTargetMonth });
  }
  return cells;
}
function formatHuman(ymd) {
  if (!ymd) return "";
  const { y, m, d } = toParts(ymd);
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(dt);
}

export default function EventDateStep({ onValidityChange }) {
  const { state, setDate } = useWeddingWizard();

  const todayStr = formatDay(new Date());
  const todayParts = todayStr.split("-").map(Number);
  const [initY, initM] = (
    state.fulfillment?.scheduledDate
      ? String(state.fulfillment.scheduledDate).slice(0, 7)
      : todayStr.slice(0, 7)
  ).split("-");

  const [year, setYear] = useState(Number(initY));
  const [month, setMonth] = useState(Number(initM)); // 1-12
  const [selectedDay, setSelectedDay] = useState(
    state.fulfillment?.scheduledDate
      ? String(state.fulfillment.scheduledDate).slice(8, 10)
      : ""
  );

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [availByDay, setAvailByDay] = useState({}); // { ymd: { remainingSpots, isBlocked, ... } }

  // when the user clicks an adjacent-month day, we momentarily skip the "clear selection on month change"
  const skipClearSelectionOnce = useRef(false);

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y + 1];
  }, []);

  const fetchMonth = useCallback(async (y, m) => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/weddingAvailability/month", {
        params: { year: y, month: m },
      });
      if (!data?.ok || !Array.isArray(data?.days)) {
        throw new Error(data?.error || "Failed to load availability");
      }
      const map = {};
      for (const d of data.days) map[d.day] = d;
      setAvailByDay(map);
    } catch (e) {
      setErr(
        (e?.response?.data?.error && String(e.response.data.error)) ||
          e?.message ||
          "Failed to load availability"
      );
      setAvailByDay({});
    } finally {
      setLoading(false);
    }
  }, []);

  // load when year/month changes
  useEffect(() => {
    fetchMonth(year, month);
    if (skipClearSelectionOnce.current) {
      // preserve selection when we intentionally jumped months due to a cell click
      skipClearSelectionOnce.current = false;
    } else {
      setSelectedDay(""); // normal month change clears selection
    }
  }, [year, month, fetchMonth]);

  // validity: a date is selected
  const isValid = Boolean(selectedDay);
  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  // select a day (works for current or adjacent months)
  const handlePick = (ymdStr, inTargetMonth, disabled) => {
    if (disabled) return;
    const [Y, M, D] = ymdStr.split("-").map((n) => Number(n));
    const dd = String(D).padStart(2, "0");

    if (!inTargetMonth) {
      // jump and KEEP selection
      skipClearSelectionOnce.current = true;
      setYear(Y);
      setMonth(M);
    }
    setSelectedDay(dd);
    setDate(`${Y}-${String(M).padStart(2, "0")}-${dd}`);
  };

  // controls
  const goPrevMonth = () => {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  };
  const goNextMonth = () => {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  };
  const jumpToday = () => {
    const [Y, M] = todayParts;
    setYear(Y);
    setMonth(M);
  };

  const title = `${MONTH_NAMES[month - 1]} ${year}`;
  const isToday = (ymd) => ymd === todayStr;

  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Event Date
      </Typography>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 2 }}>
          {/* Controls row */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
              mb: 2,
            }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="year-label">Year</InputLabel>
              <Select
                labelId="year-label"
                label="Year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}>
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="month-label">Month</InputLabel>
              <Select
                labelId="month-label"
                label="Month"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTH_NAMES.map((name, idx) => (
                  <MenuItem key={name} value={idx + 1}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                ml: "auto",
              }}>
              <IconButton
                aria-label="previous month"
                onClick={goPrevMonth}
                size="small">
                <ArrowBackIosNewIcon fontSize="inherit" />
              </IconButton>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ minWidth: 140, textAlign: "center" }}>
                {title}
              </Typography>
              <IconButton
                aria-label="next month"
                onClick={goNextMonth}
                size="small">
                <ArrowForwardIosIcon fontSize="inherit" />
              </IconButton>
              <Button
                size="small"
                startIcon={<TodayIcon />}
                onClick={jumpToday}
                sx={{ ml: 1 }}>
                Today
              </Button>
            </Box>
          </Box>

          {/* Legend (neutralized) */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <Chip size="small" label="Available" variant="outlined" />
            <Chip
              size="small"
              label="Selected"
              color="primary"
              variant="filled"
            />
            <Chip size="small" label="Full/Blocked" variant="outlined" />
            <Chip size="small" label="Today" variant="outlined" />
          </Box>

          {err && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          )}

          <Box sx={{ position: "relative", minHeight: 280 }}>
            {loading ? (
              <Box sx={{ display: "grid", placeItems: "center", height: 280 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                {/* Header row */}
                <Grid container columns={7} spacing={0.75} sx={{ mb: 0.5 }}>
                  {DOW.map((d) => (
                    <Grid key={d} item xs={1}>
                      <Box
                        sx={{
                          textAlign: "center",
                          fontSize: 12,
                          color: "text.secondary",
                        }}>
                        {d}
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* 6x7 grid — neutral styles; only selected is primary */}
                <Grid container columns={7} spacing={0.75}>
                  {grid.map((cell) => {
                    const [Y, M, D] = cell.day.split("-").map((n) => Number(n));
                    const data = availByDay[cell.day];
                    const remaining = data?.remainingSpots ?? null;
                    const isBlocked = !!data?.isBlocked;
                    const disabled =
                      isBlocked || (remaining !== null && remaining <= 0);
                    const isSelected =
                      Y === year &&
                      M === month &&
                      String(D).padStart(2, "0") === selectedDay;
                    const isCellToday = isToday(cell.day);

                    return (
                      <Grid item xs={1} key={cell.day}>
                        <Button
                          variant={isSelected ? "contained" : "outlined"}
                          size="small"
                          fullWidth
                          disabled={disabled}
                          onClick={() =>
                            handlePick(cell.day, cell.inTargetMonth, disabled)
                          }
                          sx={{
                            py: 1,
                            borderRadius: 2.25,
                            textTransform: "none",
                            justifyContent: "space-between",
                            minHeight: 58,
                            // Neutral outline + hover, lower contrast for non-target month
                            color: "text.primary",
                            borderColor: (t) => t.palette.divider,
                            backgroundColor: isSelected
                              ? undefined
                              : "transparent",
                            opacity: cell.inTargetMonth ? 1 : 0.35,
                            "&:hover": {
                              backgroundColor: isSelected
                                ? undefined
                                : "action.hover",
                              borderColor: (t) => t.palette.divider,
                            },
                            // subtle ring for "today" when not selected
                            ...(isCellToday && !isSelected
                              ? {
                                  boxShadow: "inset 0 0 0 2px rgba(0,0,0,0.12)",
                                }
                              : {}),
                          }}>
                          <Box sx={{ textAlign: "left" }}>
                            <Typography
                              component="div"
                              sx={{ fontWeight: 700, fontSize: 14 }}>
                              {String(D)}
                            </Typography>
                            <Typography
                              component="div"
                              sx={{
                                fontSize: 11,
                                opacity: disabled ? 0.55 : 0.85,
                              }}>
                              {isBlocked
                                ? "Blocked"
                                : remaining === null
                                ? "" // unknown (API failed)
                                : remaining <= 0
                                ? "Full"
                                : `${remaining} left`}
                            </Typography>
                          </Box>
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </Box>

          {state.fulfillment?.scheduledDate && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Selected: {formatHuman(state.fulfillment.scheduledDate)}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
